import React, { useEffect, useMemo, useRef } from "react";

import {
  getProfileTypeColor,
  ProfileTypeName
} from "@platformsh/observability-graph/ContinuousProfiling";
import * as d3 from "d3";

import { ObservabilityPipelineContinuousProfilingTimelinePoint } from "Api/observability/pipeline/types";

type TimelineLineProps = {
  profileTypeName: ProfileTypeName;
  points: ObservabilityPipelineContinuousProfilingTimelinePoint[];
  xScale: d3.ScaleTime<number, number, never>;
  yScale: d3.ScaleLinear<number, number, never>;
};

type D3Point = {
  x: number;
  y?: number;
};

const TimelineLine = ({
  profileTypeName,
  points,
  yScale,
  xScale
}: TimelineLineProps) => {
  const urlLineRef = useRef<SVGGElement>(null);
  const [presentData, missingData] = useMemo(() => {
    const presentData: D3Point[] = [];
    const missingData: D3Point[] = [];
    let lastPoint: D3Point | undefined = undefined;
    let lastPointWithValue: D3Point | undefined = undefined;
    for (const { date, value } of points) {
      const x = xScale(date);
      const y = value === undefined ? undefined : yScale(value);
      presentData.push({ x, y });
      if (value !== undefined) {
        if (lastPoint && lastPoint.y === undefined) {
          missingData.push({ x, y }, { x });
        }
        lastPointWithValue = { x, y };
      } else {
        if (lastPointWithValue && lastPoint && lastPoint.y !== undefined) {
          missingData.push({
            x: lastPointWithValue.x,
            y: lastPointWithValue.y
          });
        }
      }
      lastPoint = { x, y };
    }
    if (
      lastPointWithValue !== undefined &&
      lastPoint &&
      lastPoint.y === undefined
    ) {
      missingData.push({
        x: lastPoint.x,
        y: lastPointWithValue.y
      });
    }

    return [presentData, missingData];
  }, [points, xScale, yScale]);

  useEffect(() => {
    const generateLine = d3
      .line<D3Point>()
      .x(d => d.x)
      .y(d => d.y ?? 0)
      .defined(d => d.y !== undefined);

    const svg = d3.select(urlLineRef.current);
    svg.selectAll("path").remove();
    svg
      .append("path")
      .attr("d", generateLine(missingData))
      .attr("stroke", getProfileTypeColor(profileTypeName))
      .attr("stroke-dasharray", "5 5")
      .attr("stroke-width", 1)
      .attr("fill", "none");
    svg
      .append("path")
      .attr("d", generateLine(presentData))
      .attr("stroke", getProfileTypeColor(profileTypeName))
      .attr("stroke-width", 1.5)
      .attr("fill", "none");
  }, [xScale, yScale, profileTypeName, presentData, missingData]);

  return <g ref={urlLineRef} />;
};

export default TimelineLine;
